import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { Link } from "@/components/catalyst/link";
import { formatDate } from "@/lib/Formatters/formatDate";
import {
  InvoiceListItem,
  invoiceListQuery,
  InvoiceListQueryVariables,
} from "../GraphQL/invoiceListQuery";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";

import { Badge } from "@/components/catalyst/badge";
import { invoiceStatusIntentMap } from "@/app/Invoices/Utils/invoiceStatusIntentMap";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { useInvoiceActions } from "@/app/Invoices/Hooks/useInvoiceActions";
import {
  InvoiceStatus,
  QueryInvoicesOrderByColumn,
  QueryInvoicesOrderByOrderByClause,
  QueryInvoicesWhereColumn,
  SortOrder,
  SqlOperator,
} from "@/gql/graphql";
import {
  MultiSelectFilter,
  MultiSelectFilterOption,
} from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { ReactNode, useEffect, useState } from "react";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { Highlighter } from "@/lib/Components/Highlighter";
import { BillingEntityFilter } from "@/app/Invoices/Components/BillingEntityFilter";
import { useTranslations } from "use-intl";

type InvoiceTableProps = {
  queryVariables?: Partial<InvoiceListQueryVariables>;
  id: TableId;
  hiddenColumns?: InvoiceTableColumnId[];
  buttons?: ReactNode;
  title?: string;

  //Uncontrolled
  defaultEnabledStatuses?: InvoiceStatus[];

  //Controlled
  statuses?: InvoiceStatus[];
  onStatusChange?: (status: InvoiceStatus[]) => void;
};

const options: MultiSelectFilterOption<InvoiceStatus>[] = [
  {
    label: "Issued",
    value: InvoiceStatus.Issued,
  },
  {
    label: "Overdue",
    value: InvoiceStatus.Overdue,
  },
  {
    label: "Paid",
    value: InvoiceStatus.Paid,
  },
  {
    label: "Voided",
    value: InvoiceStatus.Voided,
  },
  {
    label: "Draft",
    value: InvoiceStatus.Draft,
  },
];

type InvoiceTableColumnId =
  | "reference"
  | "status"
  | "payee"
  | "amount"
  | "due_date"
  | "created_at"
  | "actions";

export function InvoiceTable({
  queryVariables,
  id,
  hiddenColumns,
  defaultEnabledStatuses = [],
  buttons,
  statuses,
  onStatusChange,
  title,
}: InvoiceTableProps) {
  "use no memo";

  const getActions = useInvoiceActions();
  const { supplier } = useGlobalSupplier();
  const t = useTranslations("invoice");

  const [localStatus, setStatus] = useState<InvoiceStatus[]>(
    statuses ?? defaultEnabledStatuses,
  );
  const [billingEntityIds, setBillingEntityIds] = useState<string[]>([]);

  const status = statuses ?? localStatus;

  useEffect(() => {
    onStatusChange?.(localStatus);
  }, [localStatus]);

  const columns: DataTableColDef<InvoiceListItem, any, InvoiceTableColumnId>[] =
    [
      {
        id: "reference",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Invoice" />
        ),
        accessorFn: (row) => row.reference,
        cell: ({ row, table }) => {
          const search = table.options.meta?.search ?? "";
          return (
            <Link
              className="text-blue-500 underline"
              to={"/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id"}
              params={(prev) => ({
                ...prev,
                id: row.original.id,
              })}
              search={{}}
            >
              <Highlighter searchString={search}>
                {row.original.reference}
              </Highlighter>
            </Link>
          );
        },
      },
      {
        id: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        accessorFn: (row) => row.status,
        cell: ({ row }) => {
          return (
            <Badge color={invoiceStatusIntentMap[row.original.status]}>
              {t(`status.${row.original.status}`)}
            </Badge>
          );
        },
      },
      {
        id: "payee",
        header: "Payee",
        cell: ({ row, table }) => {
          const search = table.options.meta?.search ?? "";
          return (
            <div className="text-gray-500">
              <Highlighter searchString={search}>
                {row.original.payee.name ?? "--"}
              </Highlighter>
            </div>
          );
        },
      },
      {
        id: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Amount" />
        ),
        accessorFn: (row) => formatCurrency(row.amount_inc_tax, row.currency),
        cell: DeEmphasisedCell,
      },
      {
        id: "due_date",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Due date" />
        ),
        accessorFn: (row) => formatDate(row.due_date),
        cell: DeEmphasisedCell,
      },
      {
        id: "created_at",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Created" />
        ),
        accessorFn: (row) => formatDateTimeSinceNow(row.created_at),
        cell: DeEmphasisedCell,
      },
    ];

  return (
    <DataTable
      id={id}
      initialSorting={[{ id: "created_at", desc: true }]}
      title={title}
      getActions={getActions}
      hiddenColumns={hiddenColumns}
      document={invoiceListQuery}
      rightButtons={buttons}
      filters={(table) => (
        <div className="flex space-x-2">
          <MultiSelectFilter
            label="Status"
            options={options}
            selected={status}
            onChange={(newStatus) => {
              setStatus(newStatus);
              table.setPagination((prev) => ({
                ...prev,
                pageIndex: 0,
              }));
            }}
          />
          <BillingEntityFilter
            selected={billingEntityIds}
            onChange={(newIds) => {
              setBillingEntityIds(newIds);
              table.setPagination((prev) => ({
                ...prev,
                pageIndex: 0,
              }));
            }}
          />
        </div>
      )}
      getQueryVariables={({
        pagination,
        search,
        sorting,
      }): InvoiceListQueryVariables => {
        const baseVariables: InvoiceListQueryVariables = {
          page: pagination.pageIndex,
          first: pagination.pageSize,
        };

        // Filters are hidden while searching, so searching ignores them
        if (search) {
          return {
            ...baseVariables,
            search,
          };
        }

        baseVariables.status = status.length ? status : undefined;

        if (billingEntityIds.length) {
          baseVariables.where = {
            column: QueryInvoicesWhereColumn.PayeeContactId,
            operator: SqlOperator.In,
            value: billingEntityIds,
          };
        }

        const orderBy: QueryInvoicesOrderByOrderByClause[] = sorting.map(
          (sort) => {
            let column: QueryInvoicesOrderByColumn =
              QueryInvoicesOrderByColumn.Id;
            switch (sort.id) {
              case "reference":
                column = QueryInvoicesOrderByColumn.Reference;
                break;
              case "amount":
                column = QueryInvoicesOrderByColumn.AmountIncTax;
                break;
              case "due_date":
                column = QueryInvoicesOrderByColumn.DueDate;
                break;
              case "created_at":
                column = QueryInvoicesOrderByColumn.CreatedAt;
                break;
              case "status":
                column = QueryInvoicesOrderByColumn.Status;
                break;
              default:
                console.warn(`Column not found for ${sort.id}`);
            }
            return {
              column,
              order: sort.desc ? SortOrder.Desc : SortOrder.Asc,
            };
          },
        );

        return {
          ...baseVariables,
          supplierId: supplier ? supplier.id : undefined,
          orderBy,
          ...queryVariables,
        };
      }}
      columns={columns}
      accessor={(data) => data.invoices}
    />
  );
}
